import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Empty, Input, Select, Space, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Fleet, FleetStatus, PageMeta } from '@goelectric/shared-types';
import type { FleetFilters } from '../hooks/useFleets';
import { FleetStatusBadge } from './FleetBadges';
import {
  FLEET_DOCUMENT_TYPE_LABELS,
  FLEET_STATUS_LABELS,
  formatFleetDocument,
} from '../utils/format';

/** Debounce de la búsqueda antes de golpear el backend (server-side). */
const SEARCH_DEBOUNCE_MS = 300;

const STATUS_OPTIONS: { value: FleetStatus; label: string }[] = (
  ['ACTIVE', 'INACTIVE'] as const
).map((value) => ({ value, label: FLEET_STATUS_LABELS[value] }));

export interface FleetTableProps {
  fleets: Fleet[];
  meta: PageMeta;
  loading: boolean;
  error: string | null;
  filters: FleetFilters;
  /** Muestra el CTA del empty-state; solo con permiso de escritura. */
  canCreate: boolean;
  onFiltersChange: (filters: FleetFilters) => void;
  onOffsetChange: (offset: number) => void;
  onRetry: () => void;
  onCreate: () => void;
  onSelect: (fleetId: string) => void;
}

function hasActiveFilters(filters: FleetFilters): boolean {
  return Boolean(filters.q || filters.status);
}

/**
 * Tabla CRUD de Flotas (guía de estilo §8): búsqueda, filtro por estado, paginación
 * server-side con el `meta` del contrato, empty-state con acción sugerida y badge de
 * estado + contadores de conductores/vehículos.
 */
export function FleetTable({
  fleets,
  meta,
  loading,
  error,
  filters,
  canCreate,
  onFiltersChange,
  onOffsetChange,
  onRetry,
  onCreate,
  onSelect,
}: FleetTableProps) {
  const [query, setQuery] = useState(filters.q ?? '');

  // Sincroniza la búsqueda con el backend con debounce (evita una request por tecla).
  useEffect(() => {
    const handle = window.setTimeout(() => {
      const next = query.trim();
      if ((filters.q ?? '') !== next) {
        onFiltersChange({ ...filters, q: next || undefined });
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [query, filters, onFiltersChange]);

  const filtered = hasActiveFilters(filters);

  const columns = useMemo<ColumnsType<Fleet>>(
    () => [
      {
        title: 'Flota',
        key: 'fleet',
        fixed: 'left',
        width: 280,
        render: (_, fleet) => (
          <div className="crm-cell-stack">
            <Typography.Text strong title={fleet.name}>
              {fleet.name}
            </Typography.Text>
            <Typography.Text
              type="secondary"
              title={fleet.id}
              style={{ fontFamily: 'var(--ge-font-mono)', fontSize: 12 }}
            >
              {fleet.id}
            </Typography.Text>
          </div>
        ),
      },
      {
        title: 'Documento',
        key: 'document',
        width: 200,
        render: (_, fleet) => (
          <div className="crm-cell-stack">
            <span title={formatFleetDocument(fleet)} style={{ fontFamily: 'var(--ge-font-mono)' }}>
              {formatFleetDocument(fleet)}
            </span>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {fleet.documentType ? FLEET_DOCUMENT_TYPE_LABELS[fleet.documentType] : 'NIT'}
            </Typography.Text>
          </div>
        ),
      },
      {
        title: 'Contacto',
        key: 'contact',
        width: 230,
        render: (_, fleet) => (
          <div className="crm-cell-stack">
            <span title={fleet.phone}>{fleet.phone}</span>
            <Typography.Text
              type="secondary"
              title={fleet.email || 'Sin email'}
              style={{ fontSize: 12 }}
            >
              {fleet.email || 'Sin email'}
            </Typography.Text>
          </div>
        ),
      },
      {
        title: 'Código de flota',
        dataIndex: 'codigoFlota',
        key: 'codigoFlota',
        width: 150,
        render: (value: string) => (
          <span style={{ fontFamily: 'var(--ge-font-mono)' }}>{value}</span>
        ),
      },
      {
        title: 'Conductores',
        dataIndex: 'driverCount',
        key: 'driverCount',
        width: 120,
        align: 'right',
        sorter: (a, b) => a.driverCount - b.driverCount,
      },
      {
        title: 'Vehículos',
        dataIndex: 'vehicleCount',
        key: 'vehicleCount',
        width: 110,
        align: 'right',
        sorter: (a, b) => a.vehicleCount - b.vehicleCount,
      },
      {
        title: 'Estado',
        dataIndex: 'status',
        key: 'status',
        width: 130,
        render: (status: FleetStatus) => <FleetStatusBadge status={status} />,
      },
      {
        title: 'Acciones',
        key: 'actions',
        width: 90,
        align: 'right',
        render: (_, fleet) => (
          <Button size="small" onClick={() => onSelect(fleet.id)}>
            Ver
          </Button>
        ),
      },
    ],
    [onSelect],
  );

  const clearFilters = () => {
    setQuery('');
    onFiltersChange({});
  };

  return (
    <div>
      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          allowClear
          placeholder="Buscar por nombre, NIT o código de flota"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          style={{ width: 340 }}
          aria-label="Buscar flotas"
        />
        <Select<FleetStatus | 'ALL'>
          value={filters.status ?? 'ALL'}
          onChange={(value) =>
            onFiltersChange({ ...filters, status: value === 'ALL' ? undefined : value })
          }
          style={{ width: 180 }}
          aria-label="Filtrar por estado de la flota"
          options={[{ value: 'ALL', label: 'Todos los estados' }, ...STATUS_OPTIONS]}
        />
      </Space>

      {error ? (
        <Alert
          type="error"
          showIcon
          message="No se pudieron cargar las flotas"
          description={error}
          action={
            <Button size="small" onClick={onRetry}>
              Reintentar
            </Button>
          }
        />
      ) : (
        <Table<Fleet>
          rowKey="id"
          size="middle"
          loading={loading}
          columns={columns}
          dataSource={fleets}
          scroll={{ x: 1310 }}
          rowClassName="flotas-row"
          onRow={(fleet) => ({ onClick: () => onSelect(fleet.id) })}
          pagination={{
            current: Math.floor(meta.offset / Math.max(meta.limit, 1)) + 1,
            pageSize: meta.limit,
            total: meta.total,
            showSizeChanger: false,
            showTotal: (total) => `${total} flotas`,
            onChange: (page) => onOffsetChange((page - 1) * meta.limit),
          }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={filtered ? 'Sin flotas para los filtros' : 'Aún no hay flotas'}
              >
                {filtered ? (
                  <Button onClick={clearFilters}>Limpiar filtros</Button>
                ) : (
                  canCreate && (
                    <Button type="primary" onClick={onCreate}>
                      Crear la primera flota
                    </Button>
                  )
                )}
              </Empty>
            ),
          }}
        />
      )}
    </div>
  );
}
